import {
  Autocomplete,
  TextField,
  Checkbox,
  InputAdornment,
} from '@mui/material'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useFieldFocus } from 'context/FieldFocusContext'
import { useFieldState } from 'hooks/useFieldState'

const styles = {
  '&>label': {
    fontSize: '14px',
    color: '#75787B',
    top: '-2px',
  },
  '&>label.Mui-focused': {
    fontSize: '1rem',
    color: '#2370ED',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.Mui-focused.Mui-error': {
    color: '#ea0606',
  },
  '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
    fontSize: '1rem',
    background: '#fff',
    padding: '0 5px',
    marginLeft: '-5px',
    top: 0,
  },
  '&>label.MuiFormLabel-filled.Mui-disabled': {
    top: 0,
    background:
      'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
  },
  '&>div.MuiInputBase-root': {
    padding: '0!important',
    borderRadius: '4px !important',
  },
  '&>div.MuiInputBase-root.Mui-disabled': {
    backgroundColor: '#e9ecef!important',
  },
  '&>div.MuiInputBase-root.Mui-focused': {
    boxShadow: '0 0 0 1.5px #2370ED',
  },
  '&>div>.MuiInputBase-input': {
    width: '100%!important',
    padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
    display: 'flex',
  },
  '&>div.MuiInputBase-root>svg': {
    display: 'block',
    height: '1.5em',
    width: '1.5em',
    top: 'calc(50% - 0.75em)',
  },
}

const MuiMultiSelect = ({
  showAdornment = false,
  id,
  label,
  placeholder,
  options = [],
  value = [],
  onChange,
  required,
  defaultErrorState,
  forceError,
  autoFocus,
  hiddenLabel,
  enableHelpText,
  helpText,
}) => {
  const inputRef = useRef(null)
  const fieldId = id
  const theme = useTheme()

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const { isError, isSuccess, handleOnblur } = useFieldState({
    required,
    defaultErrorState,
    validatorFunction: () => true,
    forceError,
  })

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  const handleChange = (event, newValue) => {
    const newIds = newValue.map((opt) => opt.value)
    onChange(newIds)
  }

  const getSelectedOptions = options.filter((opt) => value.includes(opt.value))

  return (
    <Autocomplete
      sx={styles}
      multiple
      disableCloseOnSelect
      options={options}
      value={getSelectedOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      onChange={handleChange}
      filterSelectedOptions
      autoHighlight
      autoFocus={autoFocus}
      onFocus={(e) => handleFocus(fieldId, e.target)}
      onBlur={(e) => {
        handleOnblur(value)
        handleBlur()
      }}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox style={{ marginRight: 8 }} checked={selected} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          sx={styles}
          {...params}
          inputRef={inputRef}
          variant="outlined"
          label={!hiddenLabel ? label : undefined}
          placeholder={placeholder}
          error={isError}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {showAdornment && (
                  <InputAdornment position="end">
                    {isError ? (
                      <ErrorOutline
                        color="error"
                        sx={{ fontSize: '24px !important' }}
                      />
                    ) : isSuccess ? (
                      <Check
                        color="success"
                        sx={{ fontSize: '24px !important' }}
                      />
                    ) : null}
                  </InputAdornment>
                )}
              </>
            ),
          }}
        />
      )}
    />
  )
}

MuiMultiSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired, // array of selected option *values* (strings)
  onChange: PropTypes.func.isRequired, // function(value: string[]) => void
  required: PropTypes.bool,
  defaultErrorState: PropTypes.bool,
  forceError: PropTypes.bool,
  autoFocus: PropTypes.bool,
  hiddenLabel: PropTypes.bool,
  enableHelpText: PropTypes.bool,
}

export default MuiMultiSelect
